/**
 * The kebab menu on a photo tile, shared by the gallery and "Shared with me".
 *
 * It exists because a row of buttons does not fit a tile: the design system gives buttons a 44px
 * minimum height, and even two of them squeeze the caption into a column one character wide.
 *
 * Labels are written by the calling page, never by a person, so they are not escaped here.
 */

/** `items`: [{ action, icon, label, danger }] — `action` is read back from `data-action`. */
export function tileMenuHtml(items) {
    return `
        <div class="tile-menu">
            <button type="button" class="tile-menu-toggle" data-action="menu"
                    aria-label="Actions for this photo" aria-haspopup="true" aria-expanded="false">
                <i class="fa-solid fa-ellipsis-vertical"></i>
            </button>
            <div class="tile-menu-list" hidden>
                ${items.map((item) => `
                    <button type="button" class="tile-menu-item${item.danger ? ' is-danger' : ''}"
                            data-action="${item.action}">
                        <i class="fa-solid ${item.icon}"></i><span>${item.label}</span>
                    </button>`).join('')}
            </div>
        </div>`;
}

/**
 * Closes every open menu except `keep`. Only one is ever open: they are positioned over
 * neighbouring tiles, so two at once overlap each other.
 */
export function closeTileMenus(keep = null) {
    document.querySelectorAll('.tile-menu-list:not([hidden])').forEach((list) => {
        if (list === keep) return;
        list.hidden = true;
        list.parentElement?.querySelector('.tile-menu-toggle')?.setAttribute('aria-expanded', 'false');
        list.closest('.gallery-item')?.classList.remove('has-menu-open');
    });
}

/** Opens this tile's menu, closing any other. */
export function toggleTileMenu(tile) {
    const list = tile.querySelector('.tile-menu-list');
    if (!list) return;

    const opening = list.hidden;
    closeTileMenus(opening ? list : null);
    list.hidden = !opening;
    tile.querySelector('.tile-menu-toggle')?.setAttribute('aria-expanded', String(opening));
    // The tile clips its own contents for its rounded corners, so the menu has to be let out of
    // it while open, and lifted above the neighbouring tiles.
    tile.classList.toggle('has-menu-open', opening);
}

/**
 * Dismissal is bound once for the page, not per tile: both pages rebuild their tiles on every
 * refresh, and a listener per tile per render would accumulate without limit.
 */
let dismissBound = false;
export function bindTileMenuDismissal() {
    if (dismissBound) return;
    dismissBound = true;

    document.addEventListener('click', (e) => {
        if (!e.target.closest('.tile-menu')) closeTileMenus();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeTileMenus();
    });
}
